import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { AssignableUsersReq } from '~weekly-planner/types/api';
import dayjs from 'dayjs';

import { AvailabilityOptions, HighlightOptions, PreferenceOptions, SortOptions, YesNoAllTypes } from '~constants/maps';

import { Worker } from '~weekly-planner/lib/common';
import { buildAssignChange } from '~weekly-planner/lib/simulate';

import { useFocusAppointment } from '~weekly-planner/hooks/useFocusAppointment';

import { Button } from '~components';
import Accordion from '~components/Accordion';
import ConfirmDialog from '~components/ConfirmDialog';
import { MultiCheckbox, Radio, ReadOnly, Select, Switch } from '~components/form-fields/inputs';
import Icon, { faCommentSms, faMinus, faPlus, faXmark } from '~components/icon';
import UnallocatedCard from '~weekly-planner/components/card/UnallocatedCard';

import { endBidding, startBidding } from '~appointments/actions/appointments';
import { useGetAssignableUsersQuery } from '~weekly-planner/actions/appointments';
import { useLazyGetUnallocatedQuery } from '~weekly-planner/actions/appointments/unallocated';
import { simulate } from '~weekly-planner/actions/simulate';
import { visualiseDistance } from '~weekly-planner/actions/visualise';

import { makeChange } from '~weekly-planner/reducers/unsaved';
import { clear as clearUsers } from '~weekly-planner/reducers/users/users';
import { clear as clearVisualise } from '~weekly-planner/reducers/visualise';
import {
  bumpRefetchKey,
  clearFocusOptions,
  focusWorker,
  mergeBiddingWorkers,
  patchParams,
  toggleBidding,
  updateBiddingWorkers,
  updateFocusOptions,
} from '~weekly-planner/reducers/weeklyPlanner';

import {
  selectFocusedAppointment,
  selectFocusedWorkerId,
  selectFocusOptions,
  selectPlannerAPIOptions,
} from '~weekly-planner/selectors';
import { selectParams as selectUnallocatedParams } from '~weekly-planner/selectors/appointments/unallocated';
import { selectAllSimulatedChanges, selectFilteredSimulatedWorkers } from '~weekly-planner/selectors/simulate';
import { selectAllChangesCount } from '~weekly-planner/selectors/unsaved';

const FocusBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const workers = useAppSelector(selectFilteredSimulatedWorkers);
  const changesCount = useAppSelector(selectAllChangesCount);
  const focused = useAppSelector(selectFocusedAppointment);
  const options = useAppSelector(selectFocusOptions);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);
  const focusWorkerId = useAppSelector(selectFocusedWorkerId);
  const data = useAppSelector(selectAllSimulatedChanges);
  const unallocatedParams = useAppSelector(selectUnallocatedParams);
  const { unfocusAndRestoreView: unfocusAppointment } = useFocusAppointment();
  const { appointment, isBiddingView, newBidders } = focused || {};
  const { id, parent_repeat_id, date, key, bidding_user_ids = [], is_bidding } = appointment || {};
  const { availabilityFilter, qualifiedFilter, preferenceFilter, highlightType, sortBy } = options;
  const [isBidding, setIsBidding] = useState<boolean>(isBiddingView);
  const [confirmBiddingOpen, setConfirmBiddingOpen] = useState(false);
  const [cancelBiddingOpen, setCancelBiddingOpen] = useState(false);

  const queryArgs = useMemo<AssignableUsersReq | typeof skipToken>(() => {
    const id = appointment?.id;
    if (id != null) return { id: String(id) };

    const key = appointment?.key;
    if (key != null) return { key: String(key) };

    return skipToken;
  }, [appointment?.id, appointment?.key]);

  const { data: assignableUsers = [], isFetching: isAssignablesLoading } = useGetAssignableUsersQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const { limit, week_start } = unallocatedParams;
  const [triggerGetUnallocated] = useLazyGetUnallocatedQuery();

  const selectOptions = useMemo(() => {
    if (!assignableUsers.length) return [];
    const sorted = [...assignableUsers].sort((a, b) => {
      if (a.is_available !== b.is_available) return a.is_available ? -1 : 1;
      if (a.is_qualified !== b.is_qualified) return a.is_qualified ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return sorted.map(({ id, name, is_qualified, is_available }) => ({
      id,
      name: `${name}${!is_qualified ? ' (NQ)' : ''}${!is_available ? ' (NA)' : ''}`,
      is_qualified,
      is_available,
    }));
  }, [assignableUsers]);

  const confirmBiddingMessages = [];
  const newBidderCount = newBidders?.length ?? 0;
  const bidderCount = bidding_user_ids?.length + newBidderCount;

  if (bidderCount > 0 && is_bidding) {
    confirmBiddingMessages.push(
      `You are about to update a bidding event with ${newBidderCount} new workers (${bidderCount} total)`,
    );
  } else if (newBidderCount > 0) {
    confirmBiddingMessages.push(`You are about to create a bidding event for ${newBidderCount} workers.`);

    if (id === null) {
      confirmBiddingMessages.push(
        `Single appointment does not exist yet. Creating this event will also create (and save) this appointment.`,
      );
    }
  }

  if (changesCount !== 0) {
    confirmBiddingMessages.push(
      `You have ${changesCount} unsaved changes. We recommend saving before creating a bidding event`,
    );
  }

  const handleUnfocus = () => {
    dispatch(clearUsers([]));
    dispatch(bumpRefetchKey([]));
    unfocusAppointment();
  };

  const getOptionStyle = (option: any) => {
    let backgroundColor = 'white';
    if (!option.is_available) backgroundColor = 'lightpink';
    else if (!option.is_qualified) backgroundColor = 'lightyellow';

    return {
      backgroundColor,
      color: 'black',
    };
  };

  const allocateWorker = () => {
    if (!focusWorkerId || !appointment) return;

    const { id, parent_repeat_id, date } = appointment;

    dispatch(
      makeChange({
        original: appointment,
        change: { id, parent_repeat_id, date, user_id: focusWorkerId },
      }),
    );
    const change = buildAssignChange(data, [{ ...appointment, user: undefined, user_id: focusWorkerId }]);
    dispatch(simulate({ ...change, week_start: dayjs(appointment.date).weekday(0), options: plannerAPIOptions }));
  };

  const handleFocusWorkerChanged = (value: any) => {
    dispatch(focusWorker(value ?? undefined));
  };

  // reset filters if focused appointment changes
  useEffect(() => {
    dispatch(clearFocusOptions({}));
    if (!focused?.appointment) {
      dispatch(clearVisualise([]));
    }
  }, [focused?.appointment?.key]);

  useEffect(() => {
    dispatch(toggleBidding({ isBiddingView: isBidding }));
  }, [isBidding]);

  useEffect(() => {
    if (!focused?.appointment) return;

    setIsBidding(focused?.appointment.is_bidding ?? false);

    let is_qualified;
    switch (qualifiedFilter) {
      case YesNoAllTypes.YES: {
        is_qualified = true;
        break;
      }
      case YesNoAllTypes.NO: {
        is_qualified = false;
        break;
      }
      default: {
        break;
      }
    }

    let is_available;
    switch (availabilityFilter) {
      case AvailabilityOptions.AVAILABLE: {
        is_available = true;
        break;
      }
      case AvailabilityOptions.UNAVAILABLE: {
        is_available = false;
        break;
      }
      default: {
        is_available = undefined;
        break;
      }
    }

    const sort_by = sortBy === SortOptions.BIDDING ? 'bidding' : undefined;

    dispatch(
      patchParams({
        focus_appointment_id: key,
        sort_by,
        filter_by: {
          focused: {
            is_available,
            is_qualified,
            preference: preferenceFilter.map((preference) => preference.toLowerCase()),
          },
        },
      }),
    );
  }, [availabilityFilter, qualifiedFilter, preferenceFilter, sortBy, focused?.appointment?.key]);

  const refreshWorkers = (isChecked: boolean) => {
    dispatch(updateBiddingWorkers({ workerIds: workers.map((worker: Worker) => worker.id), isChecked }));
  };

  const onConfirmBidding = (confirm: boolean) => {
    setConfirmBiddingOpen(false);
    if (confirm) {
      const data = {
        id,
        date,
        parent_repeat_id,
        bidding_user_ids: [...(bidding_user_ids ?? []), ...(newBidders ?? [])],
      };
      dispatch(startBidding(data));
      dispatch(mergeBiddingWorkers(null));
    }
  };

  const onCancelBidding = async (confirm: boolean) => {
    setCancelBiddingOpen(false);
    if (confirm) {
      const { payload } = await dispatch(
        endBidding({ id: focused.appointment?.id ?? focused.appointment?.parent_repeat_id }),
      );
      if (payload?.success) {
        handleUnfocus();
        setIsBidding(false);
        triggerGetUnallocated({ limit, week_start });
      }
    }
  };

  const onOptionChange = (name: string, value: any) => {
    dispatch(updateFocusOptions({ [name]: value }));
  };

  useEffect(() => {
    dispatch(clearVisualise([]));
    if (highlightType === HighlightOptions.DISTANCE) {
      visualiseWorkersWithEvents();
    }
  }, [focused?.appointment, highlightType]);

  const visualiseWorkersWithEvents = () => {
    if (focused?.appointment) {
      const { date } = focused.appointment;

      const workersWithAppointments = workers.filter((worker: Worker) => {
        if (!worker.events?.[date]?.length) return false;
        return worker.events[date].some((event) => event.type === 'appointment');
      });

      if (workersWithAppointments.length > 0) {
        workersWithAppointments.forEach((worker: Worker) => {
          dispatch(
            visualiseDistance({
              worker,
              appointment: focused.appointment,
            }),
          );
        });
      }
    }
  };

  const renderRadioOption = (
    id: string,
    label: string,
    mapOptions: { [key: string]: string },
    value: string,
    disabled = false,
  ) => {
    const options = Object.values(mapOptions).map((option) => ({ id: option, name: option }));
    return (
      <>
        <div className="col col-sm-6">{label}</div>
        <Radio
          type="radio"
          id={id}
          name={id}
          onChange={onOptionChange}
          options={options}
          disabled={disabled}
          value={options.find((option) => option.id === value)?.name}
          isFlexible={false}
          className="radio-list"
        />
      </>
    );
  };

  const renderCheckboxOption = (
    id: string,
    label: string,
    mapOptions: { [key: string]: string },
    value: string[],
    disabled = false,
  ) => {
    const options = Object.values(mapOptions).map((option) => ({ id: option, name: option }));
    return (
      <>
        <div className="col col-sm-6">{label}</div>
        <MultiCheckbox
          type="multi-checkbox"
          id={id}
          name={id}
          onChange={onOptionChange}
          options={options}
          disabled={disabled}
          value={value}
          isFlexible={false}
        />
      </>
    );
  };

  const renderViewSection = () => (
    <div className="view-options">
      <div className="option-section qualified">
        {renderRadioOption('qualifiedFilter', 'Qualified', YesNoAllTypes, qualifiedFilter)}
      </div>
      <div className="option-section availability separated">
        {renderRadioOption('availabilityFilter', 'Availability', AvailabilityOptions, availabilityFilter)}
      </div>
      <div className="option-section  preferences separated">
        {renderCheckboxOption('preferenceFilter', 'Preferences', PreferenceOptions, preferenceFilter)}
      </div>
      <div className="option-section  highlight separated">
        {renderRadioOption('highlightType', 'Highlight', HighlightOptions, highlightType)}
      </div>
      <div className="option-section sorting separated">
        {renderRadioOption('sortBy', 'Sort by', SortOptions, sortBy)}
      </div>
    </div>
  );

  const renderBiddingSection = () => (
    <div className="view-bidding">
      <div className="row qualified">
        <div className="col col-sm-8 label">Enable Bidding</div>
        <div className="col field">
          <Switch
            type="switch"
            id="unallocated_bidding_mode"
            name="unallocated_bidding_mode"
            value={isBidding ?? ''}
            onChange={() => setIsBidding(!isBidding)}
            className="d-flex m-1"
          />
        </div>
      </div>
      {isBidding && (
        <>
          <div className="row">
            <div className="col col-sm-8 label">Selected</div>
            <div className="col field">
              <ReadOnly
                type="readonly"
                id="bidding-count"
                name="bidding-count"
                className="text-center"
                value={newBidderCount}
              />
            </div>
          </div>
          <div className="button-row">
            <Button size="sm" color="success" className="w-100 " onClick={() => refreshWorkers(true)}>
              <Icon icon={faPlus} />
              <div>Select Visible</div>
            </Button>
            <Button size="sm" color="warning" className="w-100 h-100" onClick={() => refreshWorkers(false)}>
              <Icon className="me-2" icon={faMinus} />
              Clear All
            </Button>
          </div>
          <div className="row center">
            <Button
              size="sm"
              color="success"
              className="w-100"
              onClick={() => setConfirmBiddingOpen(true)}
              disabled={!newBidders?.length}
            >
              <Icon className="me-2" icon={faCommentSms} />
              {is_bidding ? 'Amend Bidding' : 'Start Bidding'}
            </Button>
          </div>
          {is_bidding && (
            <div className="row center">
              <Button size="sm" color="danger" className="w-100" onClick={() => setCancelBiddingOpen(true)}>
                <Icon className="me-2" icon={faXmark} />
                Cancel Bidding
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="focus-bar-container">
      <ConfirmDialog
        isOpen={confirmBiddingOpen}
        messages={confirmBiddingMessages}
        onClose={(confirm) => onConfirmBidding(confirm)}
      />
      <ConfirmDialog
        isOpen={cancelBiddingOpen}
        messages={[
          `Cancel bidding for Appointment #${key}? Awaiting response from ${bidding_user_ids?.length} workers`,
        ]}
        onClose={(confirm) => onCancelBidding(confirm)}
      />
      <div className="focus-card">
        <div className="w-100">{appointment && <UnallocatedCard appointment={appointment} />}</div>
        <div role="button" className="unfocus" onClick={handleUnfocus} title="Unfocus appointment">
          <Icon icon={faXmark} size="lg" color="red" />
        </div>
      </div>
      <div className="section raw">
        <Select
          key={focusWorkerId || null}
          id={`allocate-user`}
          name={`allocate-user`}
          type="select"
          placeholder={isAssignablesLoading ? 'Loading workers…' : 'Assign Worker'}
          value={focusWorkerId}
          options={selectOptions}
          className={'form-input-multi-select'}
          getOptionStyle={getOptionStyle}
          onChange={(_id, value) => handleFocusWorkerChanged(value)}
        />
        <Button className="allocate" size="sm" color="success" disabled={!focusWorkerId} onClick={allocateWorker}>
          Allocate
        </Button>
      </div>
      <Accordion
        allowMultiple={true}
        className="sections"
        items={[
          {
            header: 'Bidding',
            bodyComponent: (_props) => <div>{renderBiddingSection()}</div>,
            bodyProps: {},
          },
          {
            header: 'Options',
            bodyComponent: (_props) => <div>{renderViewSection()}</div>,
            bodyProps: {},
          },
        ]}
      />
    </div>
  );
};

export default React.memo(FocusBar);
